import type { GlobalThemeOverrides } from "naive-ui"
import type { Palette } from "./presets"
import { lighten, darken } from "./utils"
import { getReadableTextColorAA, withAlpha } from "@/utils/colors"

export function makeThemeOverrides(p: Palette): GlobalThemeOverrides {
  const primary = p.primary
  const info = p.info
  const success = p.success
  const warning = p.warning
  const error = p.error
  // const antiGlareWhite = "#F2F3F4"
  const fontSizeSmall = "1rem"
  const fontSizeMedium = "1.2rem"
  const fontSizeLarge = "1.4rem"
  const baseBgColor = "#11141f"

  const neutralText = p.neutralText
  const neutralBg = p.neutralBg

  return {
    common: {
      primaryColor: primary,
      primaryColorHover: lighten(primary, 10),
      primaryColorPressed: darken(primary, 8),
      primaryColorSuppl: lighten(primary, 18),

      infoColor: info,
      infoColorHover: lighten(info, 10),
      infoColorPressed: darken(info, 8),

      successColor: success,
      successColorHover: lighten(success, 10),
      successColorPressed: darken(success, 8),

      warningColor: warning,
      warningColorHover: lighten(warning, 10),
      warningColorPressed: darken(warning, 8),

      errorColor: error,
      errorColorHover: lighten(error, 10),
      errorColorPressed: darken(error, 8),

      baseColor: neutralBg ?? undefined,
      textColorBase: getReadableTextColorAA(neutralBg || baseBgColor),
      textColor1: getReadableTextColorAA(neutralBg || baseBgColor), // labels, tabs
      textColor2: getReadableTextColorAA(neutralBg || baseBgColor), // links & options
      textColor3: getReadableTextColorAA(neutralBg || baseBgColor), // hover

      fontSizeMedium: fontSizeMedium,
      fontSize: fontSizeMedium,
      lineHeight: "1.6",

      iconColor: primary,
      iconColorDisabled: getReadableTextColorAA(neutralBg || baseBgColor),

      bodyColor: neutralBg,
      cardColor: darken(neutralBg as string, 8),
      modalColor: darken(neutralBg as string, 8),
      popoverColor: darken(neutralBg as string, 8),
      tableColor: darken(neutralBg as string, 8),

      borderColor: `1px solid ${neutralBg}`,
    },
    Card: {
      borderColor: "2px solid transparent",
      borderRadius: "5px",
      color: "color-mix(in srgb, var(--color-neutral-bg), white 10%)",
      boxShadow: `0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.24)`,
      textColor: getReadableTextColorAA(neutralBg || baseBgColor),
    },
    Button: {
      colorPrimary: primary,
      textColorPrimary: getReadableTextColorAA(neutralBg || baseBgColor),
      rippleColor: lighten(primary, 30),
      borderRadius: "5px",
      colorHoverPrimary: lighten(neutralText as string, 20),
      borderHoverPrimary: `1px solid ${lighten(neutralText as string, 20)}`,
      colorError: error,
      textColorError: getReadableTextColorAA(error),
      borderError: error,
      textColorTertiary: getReadableTextColorAA(lighten(neutralBg as string, 10)),
      colorTertiary: lighten(neutralBg as string, 10),
      color: lighten(neutralBg as string, 10),
      textColor: getReadableTextColorAA(lighten(neutralBg as string, 10)),
      colorPressed: lighten(neutralBg as string, 5),
      borderPressed: lighten(neutralBg as string, 10),
      textColorPressed: getReadableTextColorAA(lighten(neutralBg as string, 5)),
      colorFocus: lighten(neutralBg as string, 5),
      borderFocus: lighten(neutralText as string, 20),
      textColorFocus: getReadableTextColorAA(lighten(neutralBg as string, 5)),
      colorHover: lighten(neutralBg as string, 5),
      borderHover: lighten(neutralText as string, 20),
      textColorHover: getReadableTextColorAA(lighten(neutralBg as string, 5)),
      fontSizeLarge: fontSizeMedium,
    },
    Checkbox: {
      colorChecked: primary,
      color: lighten(neutralBg as string, 10),
      checkMarkColor: getReadableTextColorAA(neutralBg || baseBgColor),
      borderFocus: `2px solid ${lighten(neutralText as string, 20)}`,
      borderHover: `1px solid ${lighten(neutralText as string, 20)}`,
    },
    Select: {
      menuBoxShadow: `0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.24)`,
      peers: {
        InternalSelection: {
          color: darken(neutralBg as string, 10),
          colorActive: darken(neutralBg as string, 20),
          fontSizeMedium: fontSizeMedium,
          arrowColor: getReadableTextColorAA(lighten(neutralBg as string, 10)),
          fontSizeLarge: fontSizeMedium,
        },
        InternalSelectMenu: {
          color: darken(neutralBg as string, 10),
          optionTextColorActive: primary,
          optionColorActive: lighten(neutralBg as string, 30),
          optionColorPending: lighten(neutralBg as string, 30),
          optionColorActivePending: lighten(neutralBg as string, 20),
          optionFontSizeMedium: fontSizeMedium,
        },
      },
    },
    Alert: {
      colorSuccess: `${withAlpha(success, 65)}`,
      borderSuccess: `1px solid ${success}`,
      iconColorSuccess: getReadableTextColorAA(lighten(success, 10)),
      titleTextColorSuccess: getReadableTextColorAA(lighten(success, 10)),
      contentTextColorSuccess: getReadableTextColorAA(lighten(success, 10)),
      colorError: lighten(error, 10),
      borderError: `1px solid ${lighten(error, 10)}`,
      iconColorError: getReadableTextColorAA(lighten(error, 10)),
      titleTextColorError: getReadableTextColorAA(lighten(error, 10)),
      contentTextColorError: getReadableTextColorAA(lighten(error, 10)),
      colorInfo: darken(info, 30),
      borderInfo: `1px solid ${lighten(info, 10)}`,
      iconColorInfo: getReadableTextColorAA(darken(info, 30)),
      titleTextColorInfo: getReadableTextColorAA(darken(info, 30)),
      contentTextColorInfo: getReadableTextColorAA(darken(info, 30)),
      colorWarning: lighten(warning, 10),
      borderWarning: `1px solid ${lighten(warning, 10)}`,
      iconColorWarning: getReadableTextColorAA(lighten(warning, 10)),
      titleTextColorWarning: getReadableTextColorAA(lighten(warning, 10)),
      contentTextColorWarning: getReadableTextColorAA(lighten(warning, 10)),
      color: lighten(neutralBg as string, 10),
      contentTextColor: getReadableTextColorAA(lighten(neutralBg as string, 10)),
      border: "0",
      fontSize: fontSizeSmall,
    },
    Form: {
      labelFontSizeTopMedium: fontSizeSmall,
    },
    Input: {
      color: lighten(neutralBg as string, 20),
      colorFocusError: lighten(error, 10),
      fontSizeMedium: fontSizeMedium,
      colorFocus: lighten(neutralBg as string, 10),
      borderFocus: `2px solid ${lighten(neutralText as string, 20)}`,
      borderHover: `1px solid ${lighten(neutralText as string, 20)}`,
      boxShadowFocus: lighten(neutralText as string, 20),
      groupLabelColor: darken(primary as string, 10),
      groupLabelTextColor: lighten(neutralText as string, 20),
      paddingMedium: "1rem",
      heightMedium: "3rem",
      boxShadow: `0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.24)`,
      borderRadius: "var(--border-radius-medium)",
    },
    Switch: {
      textColor: getReadableTextColorAA(neutralBg || baseBgColor),
      railColorActive: primary,
      railColor: lighten(neutralBg as string, 20),
    },
    Tag: {
      fontSizeSmall: fontSizeSmall,
      fontSizeMedium: fontSizeMedium,
      fontSizeLarge: "28px",
      padding: "0.1rem 0.2rem",
      heightLarge: "3rem",
      heightMedium: "1.8rem",
    },
    Tabs: {
      tabFontSizeMedium: fontSizeMedium,
      tabFontSizeCard: fontSizeMedium,
      tabFontSizeLarge: fontSizeMedium,
    },
    Popover: {
      boxShadow: 0,
    },
    Message: {
      fontSize: fontSizeMedium,
      color: lighten(neutralBg as string, 80),
      textColor: getReadableTextColorAA(lighten(neutralBg as string, 80)),

      colorSuccess: `${withAlpha(success, 70)}`,
      borderSuccess: `1px solid ${success}`,
      textColorSuccess: getReadableTextColorAA(darken(success, 10)),
      iconColorSuccess: getReadableTextColorAA(darken(success, 10)),

      colorInfo: lighten(info, 10),
      textColorInfo: getReadableTextColorAA(lighten(info, 10)),
      iconColorInfo: getReadableTextColorAA(lighten(info, 10)),

      colorWarning: lighten(warning, 10),
      textColorWarning: getReadableTextColorAA(lighten(warning, 10)),
      iconColorWarning: getReadableTextColorAA(lighten(warning, 10)),

      colorError: lighten(error, 10),
      textColorError: getReadableTextColorAA(lighten(error, 10)),
      iconColorError: getReadableTextColorAA(lighten(error, 10)),

      maxWidth: "420px",
      iconSize: "30px",
    },
    Dropdown: {
      fontSizeMedium: fontSizeMedium,
      color: neutralBg,
      optionTextColor: getReadableTextColorAA(neutralText as string),
      optionColorHover: primary,
      optionTextColorHover: getReadableTextColorAA(neutralBg || baseBgColor),
      borderRadius: "2px",
      padding: "6px",
    },
    Skeleton: {
      color: lighten(neutralBg as string, 5),
      colorEnd: lighten(neutralBg as string, 10),
    },
    Drawer: {
      bodyPadding: "12px",
      borderRadius: "0",
      headerBorderBottom: `1px solid ${lighten(neutralBg as string, 5)}`,
      titleFontSize: fontSizeLarge,
      footerBorderTop: `1px solid ${lighten(neutralBg as string, 5)}`,
      footerPadding: "0.6rem",
      headerPadding: "0.8rem",
    },
    Divider: {
      textColor: getReadableTextColorAA(neutralBg || baseBgColor, neutralText),
      fontSize: fontSizeMedium,
    },
    AutoComplete: {
      peers: {
        InternalSelectMenu: {
          color: lighten(neutralBg as string, 30),
          optionTextColor: getReadableTextColorAA(lighten(neutralBg as string, 30)),
          optionTextColorActive: getReadableTextColorAA(neutralBg || baseBgColor),
          optionTextColorPressed: getReadableTextColorAA(lighten(neutralBg as string, 30)),
          optionFontSizeMedium: fontSizeMedium,
        },
        Input: {
          color: lighten(neutralBg as string, 10),
          textColor: getReadableTextColorAA(lighten(neutralBg as string, 10)),
          colorFocusError: lighten(error, 10),
          fontSizeMedium: fontSizeMedium,
          colorFocus: lighten(neutralBg as string, 20),
          borderFocus: `2px solid ${lighten(neutralText as string, 20)}`,
          borderHover: `1px solid ${lighten(neutralText as string, 20)}`,
          boxShadowFocus: lighten(neutralText as string, 20),
          groupLabelColor: darken(primary as string, 10),
          groupLabelTextColor: getReadableTextColorAA(darken(primary as string, 10)),
        },
      },
    },
    Typography: {
      textColor: getReadableTextColorAA(neutralBg || baseBgColor),
      textColor2: "rgba(255,255,255,0.82)",
      textColor3: "rgba(255,255,255,0.56)",
      titleTextColor: getReadableTextColorAA(neutralBg || baseBgColor),
      strongTextColor: getReadableTextColorAA(neutralBg || baseBgColor),
      codeTextColor: getReadableTextColorAA(neutralBg || baseBgColor),
      liTextColor: getReadableTextColorAA(neutralBg || baseBgColor),
      blockquoteTextColor: getReadableTextColorAA(neutralBg || baseBgColor),
      fontSize: "1.2rem",
      lineHeight: "1.6",
    },
    Modal: {
      baseColor: darken(neutralBg as string, 8),
      textColorBase: getReadableTextColorAA(darken(neutralBg as string, 8)),
    },
    Collapse: {
      dividerColor: "transparent",
      boxShadow: `0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.24)`,
    },
  }
}
